import React, { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";

/**
 * TrackingHeatmap — a quiet 13-week grid that shows how many *kinds* of
 * activity the person logged each day. Not a scoreboard; a mirror.
 *
 * Levels map to warm earthy tones sampled from the clay + sand palette so
 * the visual stays inside the zen aesthetic even at maximum intensity.
 */
const LEVEL_BG = [
  "hsl(var(--sand))",           // 0 — nothing yet
  "hsl(15 22% 82%)",            // 1 — a whisper
  "hsl(15 22% 68%)",            // 2 — steady
  "hsl(15 22% 54%)",            // 3 — clay
  "hsl(15 22% 40%)",            // 4 — deep clay
];

const LEVEL_TITLE = [
  "Rest",
  "One kind logged",
  "Two kinds",
  "Three kinds",
  "Four or more kinds",
];

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_ABBR = ["Mon","Wed","Fri"]; // sparse rail

function toWeeks(cells) {
  // Group by ISO weeks starting Monday. Pad the first week with placeholders
  // so the grid always shows Mon..Sun rows top-to-bottom.
  if (!cells?.length) return [];
  const weeks = [];
  let current = null;
  for (const cell of cells) {
    const d = new Date(cell.date + "T00:00:00");
    const dow = (d.getDay() + 6) % 7; // 0=Mon..6=Sun
    if (!current || dow === 0) {
      current = Array(7).fill(null);
      weeks.push(current);
    }
    current[dow] = cell;
  }
  // Front-pad the first partial week
  if (weeks.length && weeks[0].every((c) => c === null || c === undefined)) {
    // rare, ignore
  }
  return weeks;
}

export default function TrackingHeatmap({ days = 91 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await endpoints.trackingHeatmap(days);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load rhythm");
      }
    })();
    return () => { alive = false; };
  }, [days]);

  const weeks = useMemo(() => toWeeks(data?.cells || []), [data]);

  // Month labels above each week column when the first-of-month lands inside it
  const monthLabels = useMemo(() => {
    return weeks.map((week) => {
      const first = week.find(Boolean);
      if (!first) return "";
      const d = new Date(first.date + "T00:00:00");
      // Show month name only when this week's Monday is in the first week of that month
      if (d.getDate() <= 7) return MONTH_ABBR[d.getMonth()];
      return "";
    });
  }, [weeks]);

  if (error) {
    return (
      <div className="text-xs text-[hsl(var(--muted-foreground))] italic" data-testid="tracking-heatmap-error">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="animate-pulse h-24 rounded-md bg-[hsl(var(--sand))]/60" data-testid="tracking-heatmap-loading" />
    );
  }

  const activeDays = data.active_days || 0;
  const totalDays = data.days || days;
  const pct = totalDays ? Math.round((activeDays / totalDays) * 100) : 0;

  return (
    <section
      className="p-5 border hairline rounded-md bg-[hsl(var(--paper))]"
      data-testid="tracking-heatmap"
    >
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            Your rhythm
          </div>
          <div className="font-display text-lg italic mt-0.5" data-testid="tracking-heatmap-title">
            The last {totalDays} days
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-display" data-testid="tracking-heatmap-active-days">
            {activeDays}
            <span className="text-sm text-[hsl(var(--muted-foreground))] ml-1">/ {totalDays} days</span>
          </div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
            {pct}% of days had a small choice logged
          </div>
        </div>
      </header>

      <div className="mt-5 overflow-x-auto">
        <div className="inline-block">
          {/* Month labels row */}
          <div className="flex gap-[3px] ml-6 mb-1">
            {monthLabels.map((m, i) => (
              <div
                key={i}
                className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider"
                style={{ width: 12, textAlign: "left" }}
              >
                {m}
              </div>
            ))}
          </div>

          <div className="flex gap-[3px]">
            {/* Weekday rail */}
            <div className="flex flex-col gap-[3px] mr-1 justify-between" style={{ height: 12 * 7 + 3 * 6 }}>
              {[0,1,2,3,4,5,6].map((d) => (
                <div
                  key={d}
                  className="text-[9px] text-[hsl(var(--muted-foreground))]"
                  style={{ height: 12, lineHeight: "12px" }}
                >
                  {d === 0 && "Mon"}
                  {d === 2 && "Wed"}
                  {d === 4 && "Fri"}
                </div>
              ))}
            </div>

            {weeks.map((week, wi) => (
              <div key={wi} className="flex flex-col gap-[3px]">
                {week.map((cell, di) => (
                  <div
                    key={di}
                    className="rounded-[2px] transition-transform duration-150 hover:scale-110"
                    style={{
                      width: 12,
                      height: 12,
                      background: cell ? LEVEL_BG[cell.level] : "transparent",
                      border: cell ? "1px solid hsl(var(--line) / 0.5)" : "none",
                    }}
                    title={cell ? `${cell.date} · ${LEVEL_TITLE[cell.level]}` : ""}
                    data-testid={cell ? `heatmap-cell-${cell.date}` : undefined}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="mt-4 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs text-[hsl(var(--muted-foreground))] italic max-w-md leading-relaxed">
          Each square is a day. Deeper colour means you showed up in more ways — a routine note, a habit, a workout, a
          peptide, a journal entry.
        </p>
        <div className="flex items-center gap-1.5 text-[10px] text-[hsl(var(--muted-foreground))]" data-testid="heatmap-legend">
          <span className="uppercase tracking-wider">Less</span>
          {LEVEL_BG.map((c, i) => (
            <span
              key={i}
              className="rounded-[2px] inline-block"
              style={{ width: 12, height: 12, background: c, border: "1px solid hsl(var(--line) / 0.5)" }}
              aria-label={LEVEL_TITLE[i]}
            />
          ))}
          <span className="uppercase tracking-wider">More</span>
        </div>
      </footer>
    </section>
  );
}
